import { AppError, AppErrorCode } from '@repo/error';
import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useCallback } from 'react';
import { Link, useNavigate, useParams, useRouteError } from 'react-router';

type ErrorCodeMap = Record<number, { subHeading: string; heading: string; message: string }>;

export type GenericErrorLayoutProps = {
  children?: React.ReactNode;
  errorCode?: number;
  errorCodeMap?: ErrorCodeMap;
  /**
   * The primary button to display. If start as undefined, the default home link will be displayed.
   *
   * Set to null if you want no button.
   */
  primaryButton?: React.ReactNode | null;

  /**
   * The secondary button to display. If start as undefined, the default back button will be displayed.
   *
   * Set to null if you want no button.
   */
  secondaryButton?: React.ReactNode | null;
};

export const defaultErrorCodeMap: ErrorCodeMap = {
  404: {
    get subHeading() {
      return t('workspace.404NotFound');
    },
    get heading() {
      return t('workspace.oopsSomethingWentWrong');
    },
    get message() {
      return t('workspace.thePageYouAreLookingForWasMovedRemoved');
    },
  },
  500: {
    get subHeading() {
      return t('workspace.500InternalServerError');
    },
    get heading() {
      return t('workspace.oopsSomethingWentWrong');
    },
    get message() {
      return t('workspace.anUnexpectedErrorOccurred');
    },
  },
};

export const GenericErrorLayout = ({
  children,
  errorCode,
  errorCodeMap = defaultErrorCodeMap,
  primaryButton,
  secondaryButton,
}: GenericErrorLayoutProps) => {
  useLocale();

  const navigate = useNavigate();
  const errorMap = errorCodeMap[errorCode || 500] ?? defaultErrorCodeMap[500];

  return (
    <div className="fixed inset-0 z-0 flex h-screen w-screen items-center justify-center">
      <div className="inset-0 mx-auto flex h-full grow items-center justify-center px-6 py-32">
        <div>
          <p className="font-semibold text-muted-foreground">{errorMap?.subHeading}</p>

          <h1 className="mt-3 font-bold text-2xl md:text-3xl">{errorMap?.heading}</h1>

          <p className="mt-4 text-muted-foreground text-sm">{errorMap?.message}</p>

          <div className="mt-6 flex gap-x-2.5 gap-y-4 md:items-center">
            {secondaryButton ||
              (secondaryButton !== null && (
                <Button
                  className="w-32"
                  onClick={() => {
                    void navigate(-1);
                  }}
                  variant="ghost"
                >
                  <ChevronLeft className="me-2 h-4 w-4 rtl:rotate-180" />
                  {t('workspace.goBack')}{' '}
                </Button>
              ))}

            {primaryButton ||
              (primaryButton !== null && (
                <Button asChild>
                  <Link to={'/'}>{t('workspace.home')}</Link>
                </Button>
              ))}

            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppErrorBoundary = () => {
  useLocale();

  const error: any = useRouteError();
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const navigateTo = organizationId ? `/o/${organizationId}` : '/';

  const getError = useCallback(() => {
    if (error?.status || error?.data) {
      const appErrorCode =
        error?.status === 404 ? AppErrorCode.NOT_FOUND : AppErrorCode.UNKNOWN_ERROR;
      return AppError.toJSON({
        name: 'ErrorBoundary',
        code: appErrorCode,
        message: error?.error?.message || error?.data,
        userMessage: error?.statusText || error?.data,
        statusCode: error?.status || error?.data?.statusCode,
      });
    } else {
      return AppError.toJSON(AppError.parseError(error));
    }
  }, [error]);

  return (
    <GenericErrorLayout
      errorCode={Number(getError().statusCode) || 500}
      errorCodeMap={defaultErrorCodeMap}
      primaryButton={
        <Button
          onClick={() => {
            void navigate(navigateTo);
          }}
        >
          {t('workspace.home')}{' '}
        </Button>
      }
      secondaryButton={
        <Button
          onClick={() => {
            void navigate(-1);
          }}
          variant="secondary"
        >
          {t('workspace.goBack')}{' '}
        </Button>
      }
    />
  );
};
